'use client';

import { useState } from 'react';
import { Canvas } from '@react-three/fiber';
import { createFileRoute } from '@tanstack/react-router';
import EnvironmentSidebar from '@/components/monitoring/3d-lite/environment-sidebar';
import Scene3D from '@/components/monitoring/3d-lite/scene-3d';
import ControlOverlay from '@/components/monitoring/3d-lite/control-overlay';
import DetailsSidebar from '@/components/monitoring/3d-lite/details-sidebar';

// Environment types with sensor data
const environmentTypes = {
  home: {
    name: 'Residential Home',
    sensors: [
      {
        id: 'living_room',
        name: 'Living Room',
        position: [2, 0.1, 1],
        temperature: 22.5,
        humidity: 45.2,
        airQuality: 85,
        light: 750,
        motion: false,
      },
      {
        id: 'bedroom_main',
        name: 'Master Bedroom',
        position: [-2, 0.1, 3],
        temperature: 19.8,
        humidity: 38.1,
        airQuality: 90,
        light: 120,
        motion: false,
      },
      {
        id: 'kitchen',
        name: 'Kitchen',
        position: [-1, 0.1, -1],
        temperature: 24.2,
        humidity: 55.7,
        airQuality: 75,
        light: 890,
        motion: true,
      },
      {
        id: 'bathroom',
        name: 'Bathroom',
        position: [3, 0.1, -2],
        temperature: 23.1,
        humidity: 65.3,
        airQuality: 80,
        light: 450,
        motion: false,
      },
    ],
  },
  supermarket: {
    name: 'Retail Supermarket',
    sensors: [
      {
        id: 'produce',
        name: 'Produce Section',
        position: [-3, 0.1, 2],
        temperature: 18.5,
        humidity: 85.2,
        airQuality: 88,
        light: 1200,
        motion: true,
      },
      {
        id: 'dairy',
        name: 'Dairy Section',
        position: [3, 0.1, 2],
        temperature: 4.2,
        humidity: 45.1,
        airQuality: 92,
        light: 950,
        motion: false,
      },
      {
        id: 'frozen',
        name: 'Frozen Foods',
        position: [0, 0.1, -3],
        temperature: -18.5,
        humidity: 30.5,
        airQuality: 95,
        light: 800,
        motion: false,
      },
      {
        id: 'checkout',
        name: 'Checkout Area',
        position: [0, 0.1, 3],
        temperature: 21.8,
        humidity: 50.3,
        airQuality: 82,
        light: 1100,
        motion: true,
      },
      {
        id: 'storage',
        name: 'Storage Room',
        position: [-3, 0.1, -2],
        temperature: 16.2,
        humidity: 60.8,
        airQuality: 78,
        light: 300,
        motion: false,
      },
    ],
  },
  office: {
    name: 'Corporate Office',
    sensors: [
      {
        id: 'reception',
        name: 'Reception',
        position: [0, 0.1, 3],
        temperature: 22.1,
        humidity: 42.5,
        airQuality: 85,
        light: 950,
        motion: true,
      },
      {
        id: 'conference',
        name: 'Conference Room',
        position: [-2, 0.1, 1],
        temperature: 21.8,
        humidity: 40.2,
        airQuality: 88,
        light: 1050,
        motion: false,
      },
      {
        id: 'workspace',
        name: 'Open Workspace',
        position: [2, 0.1, 0],
        temperature: 23.2,
        humidity: 45.8,
        airQuality: 82,
        light: 1200,
        motion: true,
      },
      {
        id: 'server_room',
        name: 'Server Room',
        position: [0, 0.1, -3],
        temperature: 18.5,
        humidity: 35.2,
        airQuality: 95,
        light: 400,
        motion: false,
      },
    ],
  },
  warehouse: {
    name: 'Industrial Warehouse',
    sensors: [
      {
        id: 'loading_dock',
        name: 'Loading Dock',
        position: [-3, 0.1, -2],
        temperature: 15.8,
        humidity: 65.2,
        airQuality: 75,
        light: 600,
        motion: true,
      },
      {
        id: 'storage_a',
        name: 'Storage Area A',
        position: [2, 0.1, 2],
        temperature: 17.2,
        humidity: 55.8,
        airQuality: 80,
        light: 450,
        motion: false,
      },
      {
        id: 'storage_b',
        name: 'Storage Area B',
        position: [-2, 0.1, 2],
        temperature: 16.9,
        humidity: 58.3,
        airQuality: 78,
        light: 420,
        motion: false,
      },
      {
        id: 'office_area',
        name: 'Office Area',
        position: [3, 0.1, -1],
        temperature: 22.5,
        humidity: 42.1,
        airQuality: 88,
        light: 850,
        motion: false,
      },
    ],
  },
};

function IoT3DMonitoring() {
  const [selectedEnvironment, setSelectedEnvironment] = useState('home');
  const [selectedSensor, setSelectedSensor] = useState<any>(null);
  const [showHeatMap, setShowHeatMap] = useState(true);
  const [heatMapMetric, setHeatMapMetric] = useState('temperature');
  const [showAlerts, setShowAlerts] = useState(true);

  const currentEnvironment = environmentTypes[selectedEnvironment as keyof typeof environmentTypes];
  const sensors = currentEnvironment.sensors;

  return (
    <div className="flex h-screen bg-background">
      {/* Left Sidebar */}
      <EnvironmentSidebar
        selectedEnvironment={selectedEnvironment}
        setSelectedEnvironment={setSelectedEnvironment}
        sensors={sensors}
      />

      {/* Main 3D Viewport */}
      <div className="flex-1 relative">
        <Canvas camera={{ position: [12, 12, 12], fov: 60 }} className="bg-gradient-to-br from-background to-muted/50">
          <Scene3D
            environment={selectedEnvironment}
            sensors={sensors}
            selectedSensor={selectedSensor}
            onSensorSelect={setSelectedSensor}
            showHeatMap={showHeatMap}
            heatMapMetric={heatMapMetric}
          />
        </Canvas>

        <ControlOverlay
          selectedEnvironment={selectedEnvironment}
          showHeatMap={showHeatMap}
          setShowHeatMap={setShowHeatMap}
          heatMapMetric={heatMapMetric}
          setHeatMapMetric={setHeatMapMetric}
          showAlerts={showAlerts}
          setShowAlerts={setShowAlerts}
        />
      </div>

      {/* Right Sidebar */}
      <DetailsSidebar selectedSensor={selectedSensor} />
    </div>
  );
}

export const Route = createFileRoute('/_authenticated/(user-dashboard)/dashboard/_monitor-3d/3d-lite')({
  component: IoT3DMonitoring,
});
